import * as React from 'react';
import { useForm, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { RateUnit, WorkModel, positionCreateSchema, type PositionCreate } from '@clawhire/shared';
import { useAuth } from '@/auth/AuthProvider';
import { api, isApiError } from '@/lib/api';
import { enumOptions } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SimpleSelect } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toaster';
import { MultiSelect } from '../components/MultiSelect';
import { ownerOptions, useInvalidatePositions, useOptions, usePartners, useUsersDirectory } from '../queries';
import type { PositionDetail } from '../types';

/** The create form reuses the shared schema: field rules and normalisation come from the API contract. */
const createFormSchema = positionCreateSchema
  .pick({
    role: true,
    requestNo: true,
    intermediary: true,
    workModel: true,
    languages: true,
    startDate: true,
    endDate: true,
    submissionDeadline: true,
    clientRateEur: true,
    clientRateUnit: true,
    department: true,
    location: true,
    workload: true,
    restrictions: true,
    ownerId: true,
  })
  .extend({ context: z.string().max(8000).optional() });

type CreateInput = z.input<typeof createFormSchema>;
type CreateOutput = z.output<typeof createFormSchema>;

const DEFAULTS: CreateInput = {
  role: '',
  requestNo: '',
  intermediary: undefined,
  workModel: null,
  languages: [],
  startDate: null,
  endDate: null,
  submissionDeadline: null,
  clientRateEur: '',
  clientRateUnit: 'HOUR',
  department: '',
  location: '',
  workload: '',
  restrictions: '',
  context: '',
  ownerId: null,
};

export interface PositionCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** "New position" form → `POST /positions`, then navigates to the new detail page. */
export function PositionCreateDialog({ open, onOpenChange }: PositionCreateDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const invalidate = useInvalidatePositions();
  const intermediaries = usePartners('INTERMEDIARY');
  const users = useUsersDirectory();
  const options = useOptions();
  const [formError, setFormError] = React.useState<string | null>(null);

  const form = useForm<CreateInput, unknown, CreateOutput>({
    resolver: zodResolver(createFormSchema),
    defaultValues: DEFAULTS,
  });

  // The owner defaults to the signed-in user (the API would do the same when the field is omitted).
  const userId = user?.id ?? null;
  React.useEffect(() => {
    if (open) {
      form.reset({ ...DEFAULTS, ownerId: userId });
      setFormError(null);
    }
  }, [open, form, userId]);

  const onSubmit = async (values: CreateOutput) => {
    setFormError(null);
    const { context, ...rest } = values;
    const body: PositionCreate = {
      ...rest,
      description: context && context.trim() ? { context: context.trim() } : undefined,
    };
    try {
      const created = await api.post<PositionDetail>('/positions', body);
      toast.success(`Position ${created.requestNo} created`);
      await invalidate();
      onOpenChange(false);
      navigate(`/positions/${created.id}`);
    } catch (err) {
      if (isApiError(err) && err.status === 400) {
        const fieldErrors = err.fieldErrors();
        let applied = false;
        for (const [path, message] of Object.entries(fieldErrors)) {
          const key = path.replace(/^description\./, '') as FieldPath<CreateInput>;
          if (key in DEFAULTS) {
            form.setError(key, { type: 'server', message });
            applied = true;
          }
        }
        if (!applied) setFormError(err.message);
      } else {
        setFormError(err instanceof Error ? err.message : 'Could not create the position');
      }
    }
  };

  const languageOptions = (options.data?.language ?? []).map((v) => ({ value: v, label: v }));
  const departments = options.data?.department ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <DialogHeader>
              <DialogTitle>New position</DialogTitle>
              <DialogDescription>
                Create a job request by hand. Portal requests are usually synced by the agent.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Role</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. SAP Solution Architect" autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="requestNo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Request no</FormLabel>
                    <FormControl>
                      <Input placeholder="2600754" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="intermediary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Intermediary</FormLabel>
                    <FormControl>
                      <SimpleSelect
                        value={field.value ?? null}
                        onChange={(v) => field.onChange(v ?? undefined)}
                        options={(intermediaries.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
                        allowEmpty
                        emptyLabel="Direct client"
                        placeholder={intermediaries.isLoading ? 'Loading…' : 'Select…'}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ownerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Owner</FormLabel>
                    <FormControl>
                      <SimpleSelect
                        value={field.value ?? null}
                        onChange={(v) => field.onChange(v)}
                        options={ownerOptions(users.data, user)}
                        allowEmpty
                        placeholder={users.isLoading ? 'Loading…' : 'Select…'}
                      />
                    </FormControl>
                    <FormDescription>Who on the team is responsible for this role.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="workModel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Work model</FormLabel>
                    <FormControl>
                      <SimpleSelect
                        value={field.value ?? null}
                        onChange={(v) => field.onChange(v)}
                        options={enumOptions(WorkModel)}
                        allowEmpty
                      />
                    </FormControl>
                    <FormDescription>On-site positions are flagged and proposed as No-go.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="languages"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Languages</FormLabel>
                    <FormControl>
                      <MultiSelect
                        value={field.value ?? []}
                        onChange={field.onChange}
                        options={languageOptions}
                        allowCustom
                        placeholder="e.g. German, English"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start</FormLabel>
                    <FormControl>
                      <DateInput
                        value={typeof field.value === 'string' ? field.value : null}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End</FormLabel>
                    <FormControl>
                      <DateInput
                        value={typeof field.value === 'string' ? field.value : null}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="submissionDeadline"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Submission deadline</FormLabel>
                    <FormControl>
                      <DateInput
                        value={typeof field.value === 'string' ? field.value : null}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-[1fr_8rem] gap-2">
                <FormField
                  control={form.control}
                  name="clientRateEur"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client rate (EUR)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step="0.01"
                          placeholder="60"
                          {...field}
                          value={field.value === null || field.value === undefined ? '' : String(field.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="clientRateUnit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <FormControl>
                        <SimpleSelect
                          value={field.value ?? 'HOUR'}
                          onChange={(v) => field.onChange(v ?? 'HOUR')}
                          options={enumOptions(RateUnit)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="department"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Department</FormLabel>
                    <FormControl>
                      <Input list="position-departments" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <datalist id="position-departments">
                      {departments.map((d) => (
                        <option key={d} value={d} />
                      ))}
                    </datalist>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Munich / remote" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="workload"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Workload</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 100 %, 5 days/week" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="restrictions"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Restrictions</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={2}
                        placeholder="e.g. EU citizens only, no subcontracting"
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="context"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Context</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={4}
                        placeholder="What the client needs, in a few sentences."
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormDescription>
                      Responsibilities and must-haves can be filled in on the position page.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {formError ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {formError}
              </p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={form.formState.isSubmitting}>
                Create position
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
